import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _LayerManager;
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { LayerGroup } from "abreact/incubator/ui/layer/LayerGroup";
import { AbIterable } from "abweb/collection/AbIterable";
export class LayerManager {
  /**
   * Converts a LayerGroup ID into the corresponding CSS variable.
   * @param id
   */
  static idToCssVariable(id) {
    return `--ab-global-z-index-${id}`;
  }

  /**
   * Returns the LayerGroup for the given ID. If it doesn't exist, it is created.
   * @param id
   */
  static getLayerGroup(id) {
    const group = LayerManager.layerGroups.get(id);
    if (!group) {
      const newLayerGroup = new LayerGroup(id);
      LayerManager.layerGroups.set(id, newLayerGroup);
      return newLayerGroup;
    }
    return group;
  }

  /**
   * Gets the LayerGroup for the given ID. If it doesn't exist, returns undefined.
   * @param id
   */
  static getOptionalLayerGroup(id) {
    if (this.hasLayerGroup(id)) return this.getLayerGroup(id);
    return undefined;
  }
  static hasLayerGroup(id) {
    return LayerManager.layerGroups.has(id);
  }
  static get defaultLayerGroup() {
    return LayerManager.getLayerGroup(LayerManager.DEFAULT_GROUP_ID);
  }

  /**
   * Avoid this function, it shouldn't often be necessary. Allows the creation of a custom LayerGroup where the
   * appRootElement and layerRootElement aren't in the default locations.
   * @param id
   * @param appRootElement
   * @param layerRootElement
   */
  static makeCustomLayerGroup(id, appRootElement, layerRootElement) {
    const retrieved = LayerManager.layerGroups.get(id);
    if (retrieved) {
      return retrieved;
    }
    const newLayerGroup = new LayerGroup(id, appRootElement, layerRootElement);
    LayerManager.layerGroups.set(id, newLayerGroup);
    return newLayerGroup;
  }

  /**
   * Returns the layer for the specified id.
   * @param id
   */
  static getLayer(id) {
    for (const group of LayerManager.layerGroups.values()) {
      if (group.getLayer(id)) {
        return group.getLayer(id);
      }
    }
    return undefined;
  }

  /**
   * Returns the topmost non-login layer. Not intended for common use.
   */
  static get safeTopmostLayer() {
    // RSF - Temporarily replace reference to AbLogin.LOGIN_LAYER_GROUP_ID with hard-coded string to break import cycle.
    // TODO: Someone (Sam?) do long-term solution
    return LayerManager.getLayers().find(layer => layer.layerGroupId !== "login-dialog") ?? null;
  }

  /**
   * Iterates through all LayerGroups to find the topmost layer. Not safe for common use, as it may
   * result in the login dialog being hidden or security issues.
   */
  static get UNSAFE_topmostLayer() {
    return LayerManager.getLayers().first() ?? null;
  }

  /**
   * Returns an iterable of layers from the top down. Undefined behavior when layers are mutated during iteration.
   */
  static getLayers() {
    return AbIterable.from(LayerManager.layersGenerator());
  }

  /**
   * Generator that yields layers from top down. Undefined behavior when layers are mutated during iteration.
   */

  /**
   * Removes the given Layer or the Layer with the given ID. Returns true if removal was successful, false if
   * the Layer was not found.
   * @param layer
   */
  static remove(layer) {
    for (const group of LayerManager.layerGroups.values()) {
      if (group.remove(layer)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Destroy the layer groups and remove them (mostly useful for testing)
   */
  static destroy() {
    LayerManager.layerGroups.forEach(group => group.destroy());
    LayerManager.layerGroups = new Map();
  }

  /**
   * Destroy and remove a layer group (mostly useful for testing or switching pages)
   */
  static destroyLayerGroup(id) {
    const retrieved = LayerManager.layerGroups.get(id);
    if (retrieved) {
      retrieved.destroy();
      LayerManager.layerGroups.delete(id);
      return true;
    }
    return false;
  }
  static get isDefaultLayerGroupUsable() {
    return !!LayerManager.layerGroups.get(LayerManager.DEFAULT_GROUP_ID) && !!document.getElementById(LayerGroup.DEFAULT_APP_ROOT_ID);
  }
  static get layerCount() {
    return AbIterable.from(LayerManager.layerGroups.values()).reduce((acc, curr) => acc + curr.layers.length, 0);
  }
}
_LayerManager = LayerManager;
// Matches CSS variable found in abinitioStyleDefaults.css
_defineProperty(LayerManager, "DEFAULT_GROUP_ID", "react-layer-manager");
_defineProperty(LayerManager, "layerGroups", new Map());
_defineProperty(LayerManager, "modalPopups", new Map());
_defineProperty(LayerManager, "layersGenerator", function* () {
  const layerGroupsByZIndex = Array.from(_LayerManager.layerGroups.values()).sort((group1, group2) => {
    return group2.zIndex - group1.zIndex; // sorted from topmost to bottommost
  });
  for (const group of layerGroupsByZIndex) {
    for (const layer of group.layers.slice().reverse()) {
      yield layer;
    }
  }
});
_defineProperty(LayerManager, "addModalPopup", (parent, id) => {
  const modalSet = _LayerManager.modalPopups.get(parent) ?? new Set();
  if (modalSet.has(id)) throw new Error("Added same popup twice");
  if (modalSet.size === 0) {
    // Make the application's content invisible to screen readers 
    parent.setAttribute("aria-hidden", "true");
    _LayerManager.modalPopups.set(parent, modalSet);
  }
  modalSet.add(id);
  return () => _LayerManager.removeModalPopup(parent, id);
});
_defineProperty(LayerManager, "removeModalPopup", (parent, id) => {
  const modalSet = _LayerManager.modalPopups.get(parent);
  if (!modalSet || !modalSet.has(id)) throw new Error("Removed popup that wasn't tracked");
  modalSet.delete(id);
  if (modalSet.size === 0) {
    _LayerManager.modalPopups.delete(parent);
    // Make the application's content visible to screen readers again
    parent.setAttribute("aria-hidden", "false");
  }
});
//# sourceMappingURL=LayerManager.js.map
